import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAdminStore, toast } from '../../stores';
import { adminAuthApi } from '../../services/api';
import { NexusButton, NexusInput, NexusCard } from '../../components/nexus-ui';
import { Lock } from '@phosphor-icons/react';
import { CognaraLogo } from '../../components/instrument/CognaraLogo';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { setAuth, isAuthenticated } = useAdminStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) navigate('/admin/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await adminAuthApi.login(email, password);
      const { access_token, user } = response.data;
      setAuth(access_token, user);
      toast.success('Welcome back!');
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials');
      toast.error('Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-nexus-bg-primary relative overflow-hidden flex items-center justify-center p-4">
      <div className="absolute inset-0 opacity-30">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, rgba(30, 78, 140, 0.1) 0%, transparent 50%),
                             radial-gradient(circle at 75% 75%, rgba(242, 107, 31, 0.1) 0%, transparent 50%)`,
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
        className="w-full max-w-md z-10"
      >
        <NexusCard variant="glass" hover={false} className="p-8">
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center mb-4"
              style={{ background: 'var(--layer-2)', border: '1px solid var(--hairline)', color: 'var(--nexus-text-primary)' }}
            >
              <CognaraLogo variant="icon" size={32} />
            </motion.div>
            <h1 className="font-heading text-2xl font-bold text-nexus-text-primary">
              Cognara Admin
            </h1>
            <p className="text-nexus-text-secondary text-sm mt-2">
              Sign in to access the platform control center
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <NexusInput
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@cognara.ai"
              error={error && !email ? 'Email is required' : ''}
              data-testid="login-email"
              autoFocus
              required
            />

            <NexusInput
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              error={error}
              data-testid="login-password"
              required
            />

            <NexusButton
              type="submit"
              className="w-full"
              isLoading={isLoading}
              data-testid="login-submit"
            >
              <Lock className="w-4 h-4" weight="bold" />
              Sign In
            </NexusButton>
          </form>

          <p className="text-center text-xs text-nexus-text-tertiary mt-6">
            Protected admin area. Unauthorized access is prohibited.
          </p>
        </NexusCard>
      </motion.div>
    </div>
  );
};

export default AdminLogin;
